import { useState } from 'react';

import { StatusBanner } from '../../shared/ui/StatusBanner';

import { signInWithGoogle } from './google-signin';
import s from './GoogleButton.module.css';

interface GoogleButtonProps {
  /** Verb shown before "with Google" — "Sign in" or "Sign up". */
  label?: string;
}

/**
 * The official Google "G" is drawn inline rather than loaded from Google's CDN:
 * the CSP allows only self-hosted scripts and images, and Google's branding
 * guidelines require the mark be used unmodified — an inline SVG satisfies both.
 */
export function GoogleButton({ label = 'Sign in' }: GoogleButtonProps): JSX.Element {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleClick(): Promise<void> {
    setError(null);
    setPending(true);
    try {
      await signInWithGoogle();
      // On success the browser navigates away; nothing after this runs.
    } catch (err) {
      setPending(false);
      setError(
        err instanceof Error ? err.message : 'Could not start Google sign-in. Please try again.',
      );
    }
  }

  return (
    <>
      <button
        type="button"
        className={s.root}
        disabled={pending}
        aria-busy={pending || undefined}
        onClick={() => {
          void handleClick();
        }}
      >
        <svg className={s.mark} viewBox="0 0 18 18" aria-hidden="true">
          <path
            fill="#4285F4"
            d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.91c1.7-1.57 2.69-3.88 2.69-6.62z"
          />
          <path
            fill="#34A853"
            d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.91-2.26c-.81.54-1.84.86-3.05.86-2.35 0-4.34-1.58-5.05-3.71H.96v2.33A9 9 0 0 0 9 18z"
          />
          <path
            fill="#FBBC05"
            d="M3.95 10.71a5.41 5.41 0 0 1 0-3.42V4.96H.96a9 9 0 0 0 0 8.08l2.99-2.33z"
          />
          <path
            fill="#EA4335"
            d="M9 3.58c1.32 0 2.51.45 3.44 1.35l2.58-2.59C13.46.89 11.43 0 9 0A9 9 0 0 0 .96 4.96l2.99 2.33C4.66 5.16 6.65 3.58 9 3.58z"
          />
        </svg>
        <span>{pending ? 'Redirecting…' : `${label} with Google`}</span>
      </button>
      {error !== null ? (
        <StatusBanner tone="error" className={s.error ?? ''}>
          {error}
        </StatusBanner>
      ) : null}
    </>
  );
}
